/**
 * Tipos compartilhados e helpers do sistema de formulários que rodam tanto no
 * cliente quanto no servidor. NÃO marque este arquivo como `server-only` e não
 * importe nada de servidor aqui — `forms.ts` (server-only) e os componentes
 * client (`DynamicFormClient`, `WhatsAppPopover`) dependem dele.
 */

export type FormFieldType =
  | "TEXT"
  | "EMAIL"
  | "TEL"
  | "TEXTAREA"
  | "SELECT"
  | "RADIO"
  | "CHECKBOX"
  | "CHECKBOX_GROUP"
  | "NUMBER"
  | "DATE"
  | "TIME"
  | "URL"
  | "CPF_CNPJ"
  | "CEP"
  | "RATING"
  | "FILE"
  | "SECTION"
  | "HIDDEN"
  | "CONSENT";

export type FormSubmitBehavior = "MESSAGE" | "REDIRECT" | "WHATSAPP";

export interface FieldOption {
  value: string;
  label: string;
}

// --- Lógica condicional (condições compostas) ------------------------------

export type ConditionOperator = "equals" | "not_equals" | "contains" | "filled" | "empty";

/** Uma regra: "campo X [operador] valor". */
export interface ConditionRule {
  field: string;
  operator: ConditionOperator;
  value?: string;
}

/** Visibilidade condicional — várias regras combinadas por E/OU. */
export interface VisibilityRule {
  logic: "AND" | "OR";
  rules: ConditionRule[];
}

/** Mapa de autopreenchimento de um campo CEP → nomes de campos de endereço. */
export interface CepAutofill {
  street?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
}

/** Configuração de um campo — guardada no array `FormDefinition.fields` (JSON). */
export interface FieldConfig {
  /** chave de máquina, única no formulário (usada como `name` do input) */
  name: string;
  label: string;
  type: FormFieldType;
  placeholder?: string;
  required: boolean;
  order: number;
  /** SELECT / RADIO / CHECKBOX_GROUP */
  options?: FieldOption[];
  maxLength?: number;
  minLength?: number;
  min?: number;
  max?: number;
  /** regex (sem barras) aplicado a campos de texto */
  pattern?: string;
  patternMessage?: string;
  defaultValue?: string;
  /** largura no grid — "half" permite dois campos na mesma linha */
  width?: "full" | "half";
  helpText?: string;
  /** visível só quando as regras casam (lógica condicional) */
  visibleWhen?: VisibilityRule;
  /** FILE — lista de extensões aceitas, ex: ".pdf,.png" */
  accept?: string;
  /** CEP — campos de endereço preenchidos automaticamente */
  cepAutofill?: CepAutofill;
}

/** Forma serializável de um formulário entregue ao renderizador client. */
export interface PublicFormConfig {
  id: string;
  slug: string;
  name: string;
  fields: FieldConfig[];
  submitBehavior: FormSubmitBehavior;
  submitLabel: string;
  successMessage: string | null;
  consentRequired: boolean;
  consentText: string | null;
  honeypotEnabled: boolean;
  /** true → renderiza o captcha da Central de Segurança */
  captchaEnabled: boolean;
}

/** Contexto da página capturado no cliente no momento do envio. */
export interface PageContext {
  pageUrl?: string;
  pagePath?: string;
  pageTitle?: string;
  clientReferer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

// --- Máscaras --------------------------------------------------------------

/** Máscara de telefone brasileiro — (00) 0000-0000 (fixo) ou (00) 90000-0000 (móvel). */
export function maskPhone(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length > 10) {
    return d.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  if (d.length > 6) {
    return d.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
  }
  if (d.length > 2) {
    return d.replace(/(\d{2})(\d{0,4})/, "($1) $2");
  }
  return d;
}

/** Máscara de CEP — 00000-000. */
export function maskCep(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 8);
  return d.length > 5 ? d.replace(/(\d{5})(\d{0,3})/, "$1-$2") : d;
}

/** Máscara de CPF (000.000.000-00) ou CNPJ (00.000.000/0000-00). */
export function maskCpfCnpj(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return d
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

/** Valida CPF (11 díg.) ou CNPJ (14 díg.) pelos dígitos verificadores. */
export function isValidCpfCnpj(value: string): boolean {
  const d = value.replace(/\D/g, "");
  if (d.length === 11) {
    if (/^(\d)\1{10}$/.test(d)) return false;
    const calc = (len: number) => {
      let sum = 0;
      for (let i = 0; i < len; i++) sum += Number(d[i]) * (len + 1 - i);
      const r = (sum * 10) % 11;
      return r === 10 ? 0 : r;
    };
    return calc(9) === Number(d[9]) && calc(10) === Number(d[10]);
  }
  if (d.length === 14) {
    if (/^(\d)\1{13}$/.test(d)) return false;
    const calc = (len: number) => {
      const w = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      let sum = 0;
      for (let i = 0; i < len; i++) sum += Number(d[i]) * w[i];
      const r = sum % 11;
      return r < 2 ? 0 : 11 - r;
    };
    return calc(12) === Number(d[12]) && calc(13) === Number(d[13]);
  }
  return false;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// --- Contexto da página ----------------------------------------------------

const UTM_KEYS = ["source", "medium", "campaign", "term", "content"] as const;
const UTM_STORAGE_KEY = "ge_utm";

/**
 * Captura o contexto da página no cliente. UTMs são persistidas em
 * sessionStorage no primeiro acesso para sobreviverem à navegação interna.
 */
export function collectPageContext(): PageContext {
  if (typeof window === "undefined") return {};
  const ctx: PageContext = {
    pageUrl: window.location.href.slice(0, 500),
    pagePath: window.location.pathname.slice(0, 255),
    pageTitle: document.title.slice(0, 255),
    clientReferer: document.referrer ? document.referrer.slice(0, 500) : undefined,
  };

  let utm: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const k of UTM_KEYS) {
      const val = params.get(`utm_${k}`);
      if (val) utm[k] = val.slice(0, 160);
    }
    if (Object.keys(utm).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
    } else {
      const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
      if (stored) utm = JSON.parse(stored) as Record<string, string>;
    }
  } catch {
    /* sessionStorage indisponível — ignora */
  }

  if (utm.source) ctx.utmSource = utm.source;
  if (utm.medium) ctx.utmMedium = utm.medium;
  if (utm.campaign) ctx.utmCampaign = utm.campaign;
  if (utm.term) ctx.utmTerm = utm.term;
  if (utm.content) ctx.utmContent = utm.content;
  return ctx;
}

/** Ordena os campos por `order` (cópia — não muta o array recebido). */
export function sortFields(fields: FieldConfig[]): FieldConfig[] {
  return [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

// --- Lógica condicional ----------------------------------------------------

/** Normaliza `visibleWhen` — aceita o formato antigo `{field,equals}`. */
export function normalizeVisibility(raw: unknown): VisibilityRule | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  // Formato antigo: { field, equals }
  if (typeof o.field === "string" && o.field) {
    return { logic: "AND", rules: [{ field: o.field, operator: "equals", value: String(o.equals ?? "") }] };
  }
  if (!Array.isArray(o.rules)) return undefined;
  const rules: ConditionRule[] = [];
  for (const r of o.rules as Record<string, unknown>[]) {
    if (!r || typeof r.field !== "string" || !r.field) continue;
    const op = r.operator;
    const operator: ConditionOperator =
      op === "not_equals" || op === "contains" || op === "filled" || op === "empty" ? op : "equals";
    rules.push({ field: r.field, operator, value: r.value != null ? String(r.value) : "" });
  }
  if (rules.length === 0) return undefined;
  return { logic: o.logic === "OR" ? "OR" : "AND", rules };
}

function valueIsEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0) || v === false;
}

function evalRule(rule: ConditionRule, values: Record<string, unknown>): boolean {
  const v = values[rule.field];
  const target = rule.value ?? "";
  switch (rule.operator) {
    case "filled":
      return !valueIsEmpty(v);
    case "empty":
      return valueIsEmpty(v);
    case "not_equals":
      return Array.isArray(v) ? !v.map(String).includes(target) : String(v ?? "") !== target;
    case "contains":
      return Array.isArray(v)
        ? v.map(String).includes(target)
        : String(v ?? "").toLowerCase().includes(target.toLowerCase());
    default: // equals
      return Array.isArray(v) ? v.map(String).includes(target) : String(v ?? "") === target;
  }
}

/** Avalia se um campo deve estar visível dado o estado atual do formulário. */
export function isFieldVisible(field: FieldConfig, values: Record<string, unknown>): boolean {
  const vw = field.visibleWhen;
  if (!vw || !vw.rules || vw.rules.length === 0) return true;
  return vw.logic === "OR"
    ? vw.rules.some((r) => evalRule(r, values))
    : vw.rules.every((r) => evalRule(r, values));
}

// --- Validação de campo (cliente e servidor) -------------------------------

/**
 * Valida um campo isolado. Retorna a mensagem de erro ou `null` se válido.
 * Usado para a validação inline (onBlur) e como espelho do schema do servidor.
 */
export function validateField(field: FieldConfig, value: unknown): string | null {
  if (field.type === "SECTION" || field.type === "HIDDEN") return null;
  const empty = valueIsEmpty(value);
  if (field.required && empty) return `${field.label} é obrigatório`;
  if (empty) return null;

  if (typeof value === "string") {
    if (field.type === "EMAIL" && !EMAIL_RE.test(value)) return "E-mail inválido";
    if (field.type === "TEL" && value.replace(/\D/g, "").length < 8) return "Telefone inválido";
    if (field.type === "URL" && !/^https?:\/\/[^\s]+\.[^\s]+/.test(value)) return "URL inválida";
    if (field.type === "CEP" && value.replace(/\D/g, "").length !== 8) return "CEP inválido";
    if (field.type === "CPF_CNPJ" && !isValidCpfCnpj(value)) return "CPF/CNPJ inválido";
    if (field.minLength && value.length < field.minLength) {
      return `Mínimo de ${field.minLength} caracteres`;
    }
    if (field.pattern) {
      try {
        if (!new RegExp(field.pattern).test(value)) return field.patternMessage || "Formato inválido";
      } catch {
        /* regex inválido na config — ignora */
      }
    }
  }
  return null;
}

// --- Proof-of-Work ---------------------------------------------------------

/**
 * Resolve um desafio Proof-of-Work no browser: encontra um nonce tal que
 * sha256(challenge + nonce) comece com `difficulty` zeros hex.
 */
export async function solvePow(challenge: string, difficulty: number): Promise<string> {
  const prefix = "0".repeat(Math.max(1, Math.min(6, difficulty)));
  const enc = new TextEncoder();
  for (let nonce = 0; nonce < 5_000_000; nonce++) {
    const buf = await crypto.subtle.digest("SHA-256", enc.encode(`${challenge}${nonce}`));
    const hex = Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    if (hex.startsWith(prefix)) return String(nonce);
  }
  throw new Error("PoW: nonce não encontrado");
}
